import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CandlestickChartRoundedIcon from '@mui/icons-material/CandlestickChartRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import MobileScreenPreview from './MobileScreenPreview';

const items = [
  {
    icon: <CandlestickChartRoundedIcon />,
    eyebrow: 'Command center',
    title: 'Read your strategy state at a glance',
    screenTitle: 'Dashboard',
    description:
      'The main dashboard keeps chart context, active presets, and position data visible in one view so you can make changes without losing the market picture.',
    image: require('../assets/v2-app-screenshots/dashboard.png'),
    metrics: ['Pair selector', 'Preset chips', 'Live chart'],
  },
  {
    icon: <GraphicEqRoundedIcon />,
    eyebrow: 'Indicator stack',
    title: 'Layer technical signals without clutter',
    screenTitle: 'Technical Indicators',
    description:
      'Use familiar indicators like RSI, MACD, MFI, ADX, and EMA crossover while keeping the interface readable enough to act quickly.',
    image: require('../assets/v2-app-screenshots/technical-indicators-settings.png'),
    metrics: ['MACD', 'RSI', 'MFI'],
  },
  {
    icon: <HistoryRoundedIcon />,
    eyebrow: 'Backtesting',
    title: 'Validate ideas before putting them into rotation',
    screenTitle: 'Backtesting',
    description:
      'Compare strategies against recent market behavior and check whether a setup deserves capital before you deploy it.',
    image: require('../assets/v2-app-screenshots/my-backtests.png'),
    metrics: ['Historical runs', 'Preset tuning', 'Fast comparisons'],
  },
  {
    icon: <NotificationsActiveRoundedIcon />,
    eyebrow: 'Execution feedback',
    title: 'See profits, sells, and alerts in plain language',
    screenTitle: 'Profit Logs',
    description:
      'Track how positions are performing and get notified when buy or sell events fire, so the bot never feels like a black box.',
    image: require('../assets/v2-app-screenshots/profit-logs.png'),
    metrics: ['Profit snapshots', 'Sell flow', 'Push alerts'],
  },
];

export default function Features() {
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);
  const selectedFeature = items[selectedItemIndex];

  return (
    <Container id="features" sx={{ py: { xs: 8, sm: 12 } }}>
      <Grid container spacing={{ xs: 3, md: 5 }}>
        <Grid item xs={12} md={5}>
          <Typography
            variant="caption"
            sx={{ color: 'primary.light', letterSpacing: 2, textTransform: 'uppercase' }}
          >
            Platform tour
          </Typography>
          <Typography component="h2" variant="h2" sx={{ mt: 1.5, mb: 2 }}>
            AlgoTrader mobile
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Scan markets, easy switch strategy presets, review backtests, and
            understanding outcomes without digging through data.
          </Typography>
          <Stack spacing={1.5}>
            {items.map(({ icon, eyebrow, title }, index) => (
              <Card
                key={title}
                variant="outlined"
                onClick={() => setSelectedItemIndex(index)}
                sx={{
                  p: 2.25,
                  cursor: 'pointer',
                  borderRadius: '24px',
                  borderColor:
                    selectedItemIndex === index ? 'primary.main' : 'divider',
                  bgcolor:
                    selectedItemIndex === index
                      ? 'rgba(21, 155, 238, 0.08)'
                      : 'rgba(255,255,255,0.03)',
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ color: selectedItemIndex === index ? 'primary.light' : 'text.secondary' }}>
                    {icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', letterSpacing: 1.5, textTransform: 'uppercase' }}
                    >
                      {eyebrow}
                    </Typography>
                    <Typography variant="h6">{title}</Typography>
                  </Box>
                </Stack>
              </Card>
            ))}
          </Stack>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: '32px',
              bgcolor: 'rgba(255,255,255,0.03)',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'primary.light', letterSpacing: 1.6, textTransform: 'uppercase' }}
                  >
                  {selectedFeature.eyebrow}
                  </Typography>
                  <Typography variant="h4" sx={{ mt: 1 }}>
                    {selectedFeature.title}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {selectedFeature.metrics.map((metric) => (
                    <Chip
                      key={metric}
                      label={metric}
                      sx={{
                        borderRadius: '999px',
                        bgcolor: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(126, 165, 214, 0.14)',
                        color: 'text.secondary',
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
              <Typography color="text.secondary">{selectedFeature.description}</Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <MobileScreenPreview
                  title={selectedFeature.screenTitle}
                  image={selectedFeature.image}
                  alt={selectedFeature.title}
                />
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
